import { FUTURES_MODE_LABELS, getFuturesModeDescription } from '../navigation';

const DEFAULT_IMAGE_PATH = '/meta/default.svg';
const DEFAULT_DESCRIPTION = 'Trade crypto futures in real time with fast execution, clear charts, and structured risk controls.';

const STATIC_ROUTE_META = {
  '/': {
    title: 'Up Down Trading',
    description: 'Predict if price movement will finish up or down by expiry.',
    imagePath: '/meta/up-down.svg'
  },
  '/dashboard': {
    title: 'Dashboard',
    description: 'Monitor your trading activity, statistics, and platform overview.',
    imagePath: '/meta/default.svg'
  },
  '/login': {
    title: 'Login',
    description: 'Log in to Fifty Fifty and continue trading in real time.',
    imagePath: '/meta/login.svg'
  },
  '/register': {
    title: 'Register',
    description: 'Create a Fifty Fifty account to start trading crypto futures.',
    imagePath: '/meta/register.svg'
  },
  '/forgot-password': {
    title: 'Forgot Password',
    description: 'Recover access to your Fifty Fifty account securely.',
    imagePath: '/meta/forgot-password.svg'
  },
  '/verify-email': {
    title: 'Verify Email',
    description: 'Verify your email address to activate account access.',
    imagePath: '/meta/verify-email.svg'
  },
  '/reset-password': {
    title: 'Reset Password',
    description: 'Set a new password for your Fifty Fifty account.',
    imagePath: '/meta/reset-password.svg'
  },
  '/positions': {
    title: 'Open Positions',
    description: 'Track and manage your active trades across all supported instruments.',
    imagePath: '/meta/positions.svg'
  },
  '/history': {
    title: 'Trade History',
    description: 'Review past trades, payouts, and win or loss performance.',
    imagePath: '/meta/history.svg'
  },
  '/leaderboard': {
    title: 'Leaderboard',
    description: 'See top traders and compare performance rankings.',
    imagePath: '/meta/leaderboard.svg'
  },
  '/settings': {
    title: 'Settings',
    description: 'Manage your profile, security preferences, and notifications.',
    imagePath: '/meta/settings.svg'
  },
  '/deposit': {
    title: 'Deposit Funds',
    description: 'Top up your Fifty Fifty wallet and monitor incoming transfer history.',
    imagePath: '/meta/default.svg'
  },
  '/withdraw': {
    title: 'Withdraw Funds',
    description: 'Request payouts securely and track your withdrawal history.',
    imagePath: '/meta/default.svg'
  },
  '/support': {
    title: 'Support Inbox',
    description: 'Chat with wallet, trading, and verification support teams in a mobile-friendly inbox.',
    imagePath: '/meta/default.svg'
  },
  '/401': {
    title: 'Unauthenticated Access',
    description: 'You do not have permission to access this page in Fifty Fifty.',
    imagePath: '/meta/default.svg'
  }
};

const FUTURES_MODE_IMAGE_BY_MODE = {
  'high-low': '/meta/high-low.svg',
  'up-down': '/meta/up-down.svg',
  'high-low-spread': '/meta/high-low-spread.svg',
  'tap-trading': '/meta/tap-trading.svg'
};

function normalizePathname(pathname) {
  if (typeof pathname !== 'string' || pathname.length === 0) {
    return '/';
  }
  const lowercased = pathname.toLowerCase();
  if (lowercased.length > 1 && lowercased.endsWith('/')) {
    return lowercased.slice(0, -1);
  }
  return lowercased;
}

function toAbsoluteUrl(origin, path) {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const normalizedOrigin = origin?.replace(/\/+$/, '') || '';
  return `${normalizedOrigin}${normalizedPath}`;
}

function upsertMetaTag({ name, property, content }) {
  if (!content) {
    return;
  }
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    if (name) {
      element.setAttribute('name', name);
    }
    if (property) {
      element.setAttribute('property', property);
    }
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function upsertCanonicalTag(href) {
  if (!href) {
    return;
  }
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

export function getRouteMeta(pathname) {
  const normalizedPath = normalizePathname(pathname);
  if (STATIC_ROUTE_META[normalizedPath]) {
    return STATIC_ROUTE_META[normalizedPath];
  }

  if (normalizedPath.startsWith('/futures/')) {
    const mode = normalizedPath.split('/')[2] || 'up-down';
    const fallbackMode = FUTURES_MODE_LABELS[mode] ? mode : 'up-down';
    const modeLabel = FUTURES_MODE_LABELS[fallbackMode];
    const description = getFuturesModeDescription(fallbackMode) || DEFAULT_DESCRIPTION;
    return {
      title: modeLabel,
      description,
      imagePath: FUTURES_MODE_IMAGE_BY_MODE[fallbackMode] || DEFAULT_IMAGE_PATH
    };
  }

  return {
    title: 'Crypto Trading',
    description: DEFAULT_DESCRIPTION,
    imagePath: DEFAULT_IMAGE_PATH
  };
}

export function applyRouteMeta({ pathname, origin, projectName }) {
  const metadata = getRouteMeta(pathname);
  const resolvedTitle = metadata.title === projectName ? projectName : `${metadata.title} | ${projectName}`;
  const canonicalUrl = toAbsoluteUrl(origin, normalizePathname(pathname));
  const imageUrl = toAbsoluteUrl(origin, metadata.imagePath || DEFAULT_IMAGE_PATH);

  document.title = resolvedTitle;

  upsertMetaTag({ name: 'description', content: metadata.description });
  upsertMetaTag({ property: 'og:title', content: resolvedTitle });
  upsertMetaTag({ property: 'og:description', content: metadata.description });
  upsertMetaTag({ property: 'og:image', content: imageUrl });
  upsertMetaTag({ property: 'og:url', content: canonicalUrl });
  upsertMetaTag({ property: 'twitter:title', content: resolvedTitle });
  upsertMetaTag({ property: 'twitter:description', content: metadata.description });
  upsertMetaTag({ property: 'twitter:image', content: imageUrl });
}
